import React, { forwardRef } from "react";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { sanitizePhoneNumber } from "../../../helpers/phoneValidation";
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  DEFAULT_DATETIME_FORMAT,
} from "../../../helpers/constants";

const DATE_FIELD_TYPES = ["date", "time", "datetime", "datetime-local"];

const isDateFieldType = (type) => DATE_FIELD_TYPES.includes(type);

const parsePickerValue = (type, value) => {
  if (!value) {
    return null;
  }

  if (type === "date") {
    const [year, month, day] = value.split("-").map(Number);

    if ([year, month, day].some(Number.isNaN)) {
      return null;
    }

    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }

  if (type === "time") {
    const [hours = 0, minutes = 0, seconds = 0] = value.split(":").map(Number);

    if ([hours, minutes, seconds].some(Number.isNaN)) {
      return null;
    }

    const date = new Date();
    date.setHours(hours, minutes, seconds, 0);
    return date;
  }

  const [datePart, timePart = "00:00"] = value.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hours = 0, minutes = 0, seconds = 0] = timePart.split(":").map(Number);

  if ([year, month, day, hours, minutes, seconds].some(Number.isNaN)) {
    return null;
  }

  return new Date(year, month - 1, day, hours, minutes, seconds, 0);
};

const formatDatePart = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatTimePart = (date) => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
};

const formatPickerValue = (type, date) => {
  if (!date) {
    return "";
  }

  if (type === "date") {
    return formatDatePart(date);
  }

  if (type === "time") {
    return formatTimePart(date);
  }

  return `${formatDatePart(date)}T${formatTimePart(date)}`;
};

// Convert moment-style tokens to date-fns tokens used by react-datepicker
const toDateFnsFormat = (momentFormat) =>
  momentFormat
    .replace(/DD/g, "dd")
    .replace(/MM/g, "MM")
    .replace(/YYYY/g, "yyyy")
    .replace(/hh/g, "hh")
    .replace(/HH/g, "HH")
    .replace(/mm/g, "mm")
    .replace(/A/g, "aa");

const DATE_DISPLAY_FORMAT = toDateFnsFormat(DEFAULT_DATE_FORMAT);
const TIME_DISPLAY_FORMAT = toDateFnsFormat(DEFAULT_TIME_FORMAT);
const DATETIME_DISPLAY_FORMAT = toDateFnsFormat(DEFAULT_DATETIME_FORMAT);

const getDatePickerProps = (type) => {
  if (type === "date") {
    return {
      dateFormat: DATE_DISPLAY_FORMAT,
    };
  }

  if (type === "time") {
    return {
      dateFormat: TIME_DISPLAY_FORMAT,
      showTimeSelect: true,
      showTimeSelectOnly: true,
      timeCaption: "Time",
      timeFormat: toDateFnsFormat(DEFAULT_TIME_FORMAT),
      timeIntervals: 1,
    };
  }

  return {
    dateFormat: DATETIME_DISPLAY_FORMAT,
    showTimeSelect: true,
    timeCaption: "Time",
    timeFormat: toDateFnsFormat(DEFAULT_TIME_FORMAT),
    timeIntervals: 1,
  };
};

// Custom input so react-datepicker renders a Bootstrap Form.Control
// that picks up cmn_input / disabled_input / form-control styles
const DatePickerCustomInput = forwardRef(
  ({ value, onClick, onBlur, placeholder, disabled, className }, ref) => (
    <Form.Control
      ref={ref}
      className={className}
      value={value ?? ""}
      onClick={onClick}
      onBlur={onBlur}
      onChange={() => {}}
      placeholder={placeholder}
      disabled={disabled}
      readOnly
    />
  )
);
DatePickerCustomInput.displayName = "DatePickerCustomInput";

const getTimeBounds = (type, selectedDate, minDate, maxDate) => {
  if (type === "time") {
    return {
      minTime: minDate || undefined,
      maxTime: maxDate || undefined,
    };
  }

  if (!["datetime", "datetime-local"].includes(type) || !selectedDate) {
    return {};
  }

  const startOfDay = new Date(selectedDate);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date(selectedDate);
  endOfDay.setHours(23, 59, 59, 999);

  const isSameDay = (firstDate, secondDate) => {
    if (!firstDate || !secondDate) {
      return false;
    }

    return (
      firstDate.getFullYear() === secondDate.getFullYear() &&
      firstDate.getMonth() === secondDate.getMonth() &&
      firstDate.getDate() === secondDate.getDate()
    );
  };

  return {
    minTime: isSameDay(selectedDate, minDate) ? minDate : startOfDay,
    maxTime: isSameDay(selectedDate, maxDate) ? maxDate : endOfDay,
  };
};

const InputWithLabel = ({
  label,
  type = "text",
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled = false,
  customClass = "",
  required = false,
  className = "",
  min,
  max,
  error,
  message,
}) => {
  const isDateField = isDateFieldType(type);
  const selectedDate = isDateField ? parsePickerValue(type, value) : null;
  const minDate = isDateField ? parsePickerValue(type, min) : min;
  const maxDate = isDateField ? parsePickerValue(type, max) : max;
  const timeBounds = isDateField
    ? getTimeBounds(type, selectedDate, minDate, maxDate)
    : {};

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    if (type === "mobile") {
      const sanitizedValue = sanitizePhoneNumber(inputValue);
      onChange({ target: { name, value: sanitizedValue } });
    } else {
      onChange(e);
    }
  };

  const handleDatePickerChange = (date) => {
    onChange({
      target: {
        name,
        value: formatPickerValue(type, date),
      },
    });
  };

  return (
    <Form.Group className={`mb-3 ${className}`}>
      <Form.Label className="cmn_label" column={""}>
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>
      {isDateField ? (
        <DatePicker
          customInput={
            <DatePickerCustomInput
              className={`${customClass} ${
                disabled ? "disabled_input" : "cmn_input"
              } form-control`}
              disabled={disabled}
              placeholder={placeholder}
            />
          }
          selected={selectedDate}
          onChange={handleDatePickerChange}
          onBlur={onBlur}
          placeholderText={placeholder}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          name={name}
          autoComplete="off"
          required={required}
          popperPlacement="bottom-start"
          {...timeBounds}
          {...getDatePickerProps(type)}
        />
      ) : (
        <Form.Control
          className={`${customClass} ${
            disabled ? "disabled_input" : "cmn_input"
          } `}
          type={type === "number" || type === "mobile" ? "text" : type}
          onKeyDown={(e) => {
            if (type === "number") {
              const allowedKeys = [
                "Backspace",
                "Tab",
                "ArrowLeft",
                "ArrowRight",
                "Delete",
                ".",
              ];
              if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                e.preventDefault();
              }
              if (e.key === "." && e.currentTarget.value.includes(".")) {
                e.preventDefault();
              }
            } else if (type === "mobile") {
              const allowedKeys = [
                "Backspace", "Tab", "ArrowLeft", "ArrowRight",
                "ArrowUp", "ArrowDown", "Delete", "Home", "End",
                " ", "+", "-", ".", "(", ")",
              ];
              if (!allowedKeys.includes(e.key) && !/^\d$/.test(e.key)) {
                e.preventDefault();
              }
              if (e.key === "+" && e.currentTarget.selectionStart !== 0) {
                e.preventDefault();
              }
            } else {
              return true;
            }
          }}
          name={name}
          value={value ?? ""}
          onChange={handleInputChange}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          onWheel={(e) => e.target.blur()}
          min={min}
          max={max}
          step="any"
        />
      )}
      {error && <p className="text-danger">{error}</p>}
      {message && <small className="text-success text-sm">{message}</small>}
    </Form.Group>
  );
};

export default InputWithLabel;
